/**
 * Debug store for entitlement troubleshooting.
 * Tracks every entitlement state change, its origin, and invalidation events.
 * Dev-only — tree-shaken in production builds.
 */

import { create } from "zustand";
import type { EntitlementResult } from "@/types/entitlements";

export type EntitlementEventKind =
  | "set" // setEntitlements called (new data from API)
  | "clear" // setEntitlements(null)
  | "invalidate" // TanStack query invalidated (will trigger refetch)
  | "ws_event" // WebSocket entitlement event received
  | "ws_reconnect" // WebSocket reconnected (triggers invalidation)
  | "billing_switch" // Billing context changed
  | "mutation"; // Mutation triggered (create/delete entity)

export interface EntitlementEvent {
  id: number;
  ts: number; // Date.now()
  kind: EntitlementEventKind;
  source: string; // e.g. "useProjects.projectsQuery", "useWorkflowQueries.workflowQuery"
  detail?: string; // Human-readable description
  snapshot?: EntitlementResult | null; // State snapshot at time of event
  diff?: string[]; // What changed vs previous snapshot
  raw?: unknown; // Raw API/WS payload for inspection
}

interface EntitlementDebugState {
  events: EntitlementEvent[];
  maxEvents: number;
  isRecording: boolean;
  counter: number;
}

interface EntitlementDebugActions {
  log: (
    kind: EntitlementEventKind,
    source: string,
    detail?: string,
    snapshot?: EntitlementResult | null,
    raw?: unknown,
  ) => void;
  clear: () => void;
  setRecording: (on: boolean) => void;
}

function diffSnapshots(
  prev: EntitlementResult | null | undefined,
  next: EntitlementResult | null | undefined,
): string[] {
  if (!prev && !next) return [];
  if (!prev) return ["initial load"];
  if (!next) return ["cleared"];

  const changes: string[] = [];

  const gateKeysPrev = Object.keys(prev.gates);
  const gateKeysNext = Object.keys(next.gates);
  const addedGates = gateKeysNext.filter((k) => !gateKeysPrev.includes(k));
  const removedGates = gateKeysPrev.filter((k) => !gateKeysNext.includes(k));
  if (addedGates.length) changes.push(`+gates: ${addedGates.join(", ")}`);
  if (removedGates.length) changes.push(`-gates: ${removedGates.join(", ")}`);

  for (const group of ["caps", "quotas", "budgets", "rateLimits"] as const) {
    const prevKeys = Object.keys(prev[group]);
    const nextKeys = Object.keys(next[group]);
    const added = nextKeys.filter((k) => !prevKeys.includes(k));
    const removed = prevKeys.filter((k) => !nextKeys.includes(k));
    if (added.length) changes.push(`+${group}: ${added.join(", ")}`);
    if (removed.length) changes.push(`-${group}: ${removed.join(", ")}`);

    // Check value changes
    for (const key of nextKeys) {
      const p = (prev[group] as Record<string, unknown>)[key] as
        Record<string, unknown> | undefined;
      const n = (next[group] as Record<string, unknown>)[key] as
        Record<string, unknown> | undefined;
      if (p && n) {
        if (JSON.stringify(p) !== JSON.stringify(n)) {
          changes.push(`~${group}.${key} changed`);
        }
      }
    }
  }

  const prevPg = prev.parameterGates?.length ?? 0;
  const nextPg = next.parameterGates?.length ?? 0;
  if (prevPg !== nextPg) {
    changes.push(`parameterGates: ${prevPg} -> ${nextPg}`);
  }

  return changes.length ? changes : ["no diff (identical)"];
}

export const useEntitlementDebugStore = create<
  EntitlementDebugState & EntitlementDebugActions
>()((set, get) => ({
  events: [],
  maxEvents: 200,
  isRecording: true,
  counter: 0,

  log: (kind, source, detail, snapshot, raw) => {
    if (!get().isRecording) return;

    const { events, maxEvents, counter } = get();
    const prevSnapshot = [...events]
      .reverse()
      .find((e) => e.snapshot !== undefined)?.snapshot;

    const event: EntitlementEvent = {
      id: counter + 1,
      ts: Date.now(),
      kind,
      source,
      detail,
      snapshot: snapshot !== undefined ? snapshot : undefined,
      diff:
        snapshot !== undefined
          ? diffSnapshots(prevSnapshot, snapshot)
          : undefined,
      raw,
    };

    const next = [...events, event];
    set({
      events: next.length > maxEvents ? next.slice(-maxEvents) : next,
      counter: counter + 1,
    });
  },

  clear: () => set({ events: [], counter: 0 }),

  setRecording: (on) => set({ isRecording: on }),
}));
